import { useState, useEffect } from 'react';
import { INDICATOR_REGISTRY } from '../config/indicatorRegistry';
import { cachedFetchJSON, GEO_TTL } from '../utils/fetchCache';

const CENSUS_GROUPS_URL = 'https://api.census.gov/data/2023/acs/acs5/groups.json';

// CDC Places catalog — resource endpoint (api/v3 returns 403).
// Fetch without a county filter to get all measures; deduplicate client-side.
// The $ signs must be literal in the URL string (not URLSearchParams-encoded).
// $group deduplicates at the API level — without it, $limit=200 returns 200
// rows for the same first measureid (one row per county) and client-side
// dedup yields only 1 measure.
const CDC_CATALOG_URL =
  'https://data.cdc.gov/resource/swc5-untb.json' +
  '?$select=measureid,measure,category,short_question_text' +
  '&$group=measureid,measure,category,short_question_text' +
  '&$order=measureid' +
  '&$limit=200';

function toTitleCase(str) {
  return str
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bAcs\b/g, 'ACS')
    .replace(/\bSnap\b/g, 'SNAP')
    .replace(/\bCdp\b/g, 'CDP');
}

/**
 * Fetches all Census ACS groups (~1,200) and all CDC PLACES measures (~35),
 * merges registry entries (supported: true) with unsupported catalog items.
 *
 * Returns:
 *   census: all registry entries first (supported: true, live labels), then
 *           unconfigured API groups (supported: false), sorted by topic/label
 *   cdc:    all measures from API deduplicated (all supported: true), or
 *           registry fallback on failure
 *   censusError: string | null
 *   cdcError:    string | null  (separate so OutcomesTab can show per-tab)
 *   loading: boolean
 */
export function useCatalog() {
  const [census, setCensus] = useState([]);
  const [cdc, setCdc] = useState([]);
  const [svi, setSvi] = useState([]);
  const [ejscreen, setEjscreen] = useState([]);
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [censusError, setCensusError] = useState(null);
  const [cdcError, setCdcError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setCensusError(null);
      setCdcError(null);

      const censusEntries    = INDICATOR_REGISTRY.filter(r => r.source === 'census');
      const cdcEntries       = INDICATOR_REGISTRY.filter(r => r.source === 'cdc');
      const sviEntries       = INDICATOR_REGISTRY.filter(r => r.source === 'svi');
      const ejscreenEntries  = INDICATOR_REGISTRY.filter(r => r.source === 'ejscreen');
      const educationEntries = INDICATOR_REGISTRY.filter(r => r.source === 'education');

      const [censusResult, cdcResult] = await Promise.allSettled([
        cachedFetchJSON(CENSUS_GROUPS_URL, GEO_TTL),
        cachedFetchJSON(CDC_CATALOG_URL, GEO_TTL)
      ]);

      if (cancelled) return;

      // ── Census catalog ────────────────────────────────────────────────────
      // Build a Set of all census table codes that have registry entries.
      // (Multiple registry entries can share the same censusTable, e.g. B01001 for
      //  population_under_18 and population_65_plus — we must not use a Map here.)
      const registryCensusTables = new Set(censusEntries.map(e => e.censusTable));

      let censusItems;
      if (censusResult.status === 'fulfilled') {
        const groups = censusResult.value?.groups ?? [];

        // Build label lookup: table code → human-readable label from live API
        const apiLabelByTable = new Map(
          groups
            .filter(g => g.description)
            .map(g => [g.name, toTitleCase(g.description)])
        );

        // All registry entries are supported — enrich with live label where available
        const supported = censusEntries.map(entry => ({
          ...entry,
          label: apiLabelByTable.get(entry.censusTable) ?? entry.label,
          code: entry.censusTable,
          supported: true
        }));
        supported.sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));

        // API groups not covered by any registry entry — shown but not addable
        const unsupported = groups
          .filter(g => !registryCensusTables.has(g.name))
          .map(g => ({
            id: g.name.toLowerCase(),
            source: 'census',
            label: g.description ? toTitleCase(g.description) : g.name,
            code: g.name,
            topic: 'Census Tables',
            supported: false
          }));
        unsupported.sort((a, b) => a.code.localeCompare(b.code));

        censusItems = [...supported, ...unsupported];
      } else {
        setCensusError(`Census catalog unavailable — showing supported indicators only. (${censusResult.reason?.message})`);
        censusItems = censusEntries
          .map(entry => ({ ...entry, code: entry.censusTable, supported: true }))
          .sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));
      }

      // ── CDC catalog ───────────────────────────────────────────────────────
      let cdcItems;
      if (cdcResult.status === 'fulfilled') {
        // resource endpoint returns a plain array
        const rows = Array.isArray(cdcResult.value) ? cdcResult.value : [];

        // Deduplicate by measureid (rows are ordered by measureid so first occurrence wins)
        const seen = new Set();
        const unique = [];
        for (const row of rows) {
          const mid = (row.measureid || '').toUpperCase();
          if (mid && !seen.has(mid)) {
            seen.add(mid);
            unique.push(row);
          }
        }

        cdcItems = unique.map(row => ({
          id: (row.measureid || '').toLowerCase(),
          label: row.short_question_text || row.measure || row.measureid,
          topic: row.category || 'CDC Places',
          code: (row.measureid || '').toUpperCase(),
          source: 'cdc',
          cdcViewId: 'swc5-untb',
          cdcMeasureId: (row.measureid || '').toUpperCase(),
          unit: 'percent',
          years: [2019, 2020, 2021, 2022, 2023],
          supported: true
        }));
        cdcItems.sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));
      } else {
        setCdcError(`CDC catalog unavailable — showing supported indicators only. (${cdcResult.reason?.message})`);
        cdcItems = cdcEntries
          .map(entry => ({ ...entry, code: entry.cdcMeasureId, supported: true }))
          .sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));
      }

      // ── Static catalogs (registry-only, no remote API) ────────────────────
      const toStaticItem = entry => ({ ...entry, code: entry.id, supported: true });

      const sviItems = sviEntries.map(toStaticItem)
        .sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));

      const ejscreenItems = ejscreenEntries.map(toStaticItem)
        .sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));

      const educationItems = educationEntries.map(toStaticItem)
        .sort((a, b) => a.topic.localeCompare(b.topic) || a.label.localeCompare(b.label));

      setCensus(censusItems);
      setCdc(cdcItems);
      setSvi(sviItems);
      setEjscreen(ejscreenItems);
      setEducation(educationItems);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return { census, cdc, svi, ejscreen, education, loading, censusError, cdcError };
}
